import { Paper } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router';
import { createLink } from '../../GlobalApp/components';
import { themeColors } from '../../JuvareTheme';
import { LandingPage } from '../../LandingPage';
import { MarkdownStyle } from '../../MarkdownStyle';
import { NavigationContext } from '../../NavigationContext';
/**
 * Renders a single landing page view for the application.
 *
 * Supports dynamic navigation, markdown or custom content, login element,
 * and header/footer customization. Integrates with React Router and internal navigation context.
 *
 * @param {object} props - The component props.
 * @param {string} [props.image] - Optional background image URL.
 * @param {string} [props.juvareHref] - Optional link URL for the Juvare logo.
 * @param {() => void} [props.juvareOnClick] - Optional click handler for the Juvare logo.
 * @param {React.ReactNode} [props.appImage] - Optional app image element.
 * @param {React.ReactNode} props.appTitle - App title content (required).
 * @param {'local' | 'dev' | 'lab' | 'demo'} [props.environment] - Optional environment name.
 * @param {string} [props.environmentLabel] - Optional environment label override.
 * @param {string} [props.contentTitle] - Optional content section title.
 * @param {React.ReactNode} [props.contentElement] - Optional JSX element for content.
 * @param {string} [props.contentHtml] - Optional HTML string for content (used with `dangerouslySetInnerHTML`).
 * @param {boolean} [props.border] - Whether to wrap content in a Paper border.
 * @param {React.ReactNode} [props.loginElement] - Optional login component (form or button).
 * @param {LandingAppPage[]} props.pages - Array of landing pages for the header.
 * @param {string} [props.backToLoginLabel] - Label for the "back to login" link.
 * @param {string} [props.basename] - Optional router basename for path resolution.
 * @param {React.ReactNode} [props.footerCopyright] - Optional footer copyright element.
 * @returns {JSX.Element} A fully rendered landing page.
 */
export const Page = (props) => {
    const navigationContext = React.useContext(NavigationContext);
    const navigate = useNavigate();
    const headerItems = props.pages.map((page) => {
        var _a;
        return ({
            label: page.label,
            href: 'path' in page ? createLink([(_a = props.basename) !== null && _a !== void 0 ? _a : '/', page.path]) : undefined,
            onClick: () => {
                if ('path' in page) {
                    navigationContext.navigate(() => navigate('/' + page.path));
                }
                else {
                    const onClick = page.onClick;
                    navigationContext.navigate(() => onClick(navigate));
                }
            },
            'data-test': page['data-test'],
        });
    });
    let contentElement;
    if (props.contentHtml) {
        contentElement = React.createElement("div", { dangerouslySetInnerHTML: { __html: props.contentHtml } });
    }
    else {
        contentElement = props.contentElement;
    }
    return (React.createElement(LandingPage, { image: props.image, juvareHref: props.juvareHref, juvareOnClick: props.juvareOnClick, appImage: props.appImage, appTitle: props.appTitle, environment: props.environment, environmentLabel: props.environmentLabel, headerItems: headerItems, contentTitle: props.contentTitle, contentElement: (dialog) => props.border ? (React.createElement(Paper, { variant: 'outlined', sx: { p: 2 } },
            React.createElement(MarkdownStyle, { firstHeading: 'h5', textColor: dialog ? undefined : themeColors.text.subtle }, contentElement))) : (React.createElement(MarkdownStyle, { firstHeading: dialog ? 'h6' : 'h5', textColor: dialog ? undefined : themeColors.text.subtle }, contentElement)), loginElement: props.loginElement, backToLoginOnClick: props.loginElement ? undefined : () => navigationContext.navigate(() => navigate('/')), backToLoginLabel: props.backToLoginLabel, footerCopyright: props.footerCopyright }));
};
